import React from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Chip,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress
} from '@material-ui/core'
import {
  Close as CloseIcon,
  GetApp as GetAppIcon,
  ErrorOutline
} from '@material-ui/icons'
import ImportTableCard from './ImportTableCard'
import SourceCropPanel from './SourceCropPanel'


const normalizeUnit = (value) => {
  const upper = String(value || '').trim().toUpperCase()
  if (!upper) return ''
  if (upper === 'S.F.' || upper === 'SF') return 'SF'
  if (upper === 'L.F.' || upper === 'LF') return 'LF'
  if (upper === 'GAL.' || upper === 'GALLON' || upper === 'GAL') return 'GAL'
  return upper
}

const buildPreviewRowDescription = (row) => {
  if (row.description) return row.description
  if (row.item) return row.item
  const commonName = String(row.commonName || '').trim()
  const botanicalName = String(row.botanicalName || '').trim()
  if (commonName && botanicalName) return `${commonName} (${botanicalName})`
  return commonName || botanicalName || ''
}

const buildPreviewRowQuantity = (row) => {
  if (row.quantity != null) return Number(row.quantity) || 0
  if (row.qty != null) return Number(row.qty) || 0
  return 0
}

const buildPreviewRowUnit = (row) => {
  const explicit = normalizeUnit(row.unit)
  if (explicit) return explicit
  const hasQuantity = row.quantity != null || row.qty != null
  if (hasQuantity && (row.size || row.botanicalName || row.commonName)) return 'EA'
  const text = `${row.sourceWords || ''} ${row.comments || ''}`
  const match = text.match(/\b(S\.?F\.?|L\.?F\.?|SF|LF|TON|CY|EA|GAL)\b/i)
  return match ? normalizeUnit(match[1]) : ''
}

const isPreviewRowImportable = (row, overrides = {}) => {
  const merged = { ...row, ...overrides }
  const description = buildPreviewRowDescription(merged)
  const quantity = buildPreviewRowQuantity(merged)
  const unit = String(buildPreviewRowUnit(merged) || '').trim()
  const isAllowance = /^(ls|allow)$/i.test(unit)
  return Boolean(description) && Boolean(unit) && (quantity > 0 || isAllowance)
}

const isManualReviewPlaceholderTable = (table = {}) => Boolean(
  table &&
  (
    table.kind === 'manual_visual_review' ||
    table.quality?.manualReviewPlaceholder === true
  )
)

const resolveTablePageNumber = (table = {}) => {
  if (Number.isFinite(Number(table.page))) return Number(table.page)
  const rows = Array.isArray(table.rows) ? table.rows : []
  if (rows.length > 0 && Number.isFinite(Number(rows[0]?.page))) return Number(rows[0].page)
  const artifacts = Array.isArray(table.reviewArtifacts) ? table.reviewArtifacts : []
  if (artifacts.length > 0 && Number.isFinite(Number(artifacts[0]?.page))) return Number(artifacts[0].page)
  return null
}

const buildExtractedTableFallbacksByPage = (parseResult = {}) => {
  const tablesByPage = new Map()
  const seen = new Set()

  const pushTable = (table) => {
    if (!table || isManualReviewPlaceholderTable(table)) return
    const pageNumber = resolveTablePageNumber(table)
    if (!Number.isFinite(pageNumber)) return
    const rows = Array.isArray(table.rows) ? table.rows : []
    if (rows.length === 0) return
    const firstRow = rows[0] || {}
    const key = [
      pageNumber,
      table.kind || '',
      table.title || '',
      rows.length,
      firstRow.description || firstRow.item || firstRow.commonName || firstRow.botanicalName || ''
    ].join('|')
    if (seen.has(key)) return
    seen.add(key)
    if (!tablesByPage.has(pageNumber)) tablesByPage.set(pageNumber, [])
    tablesByPage.get(pageNumber).push(table)
  }

  ;(Array.isArray(parseResult.tables) ? parseResult.tables : []).forEach(pushTable)
  ;(Array.isArray(parseResult.sheets) ? parseResult.sheets : []).forEach((sheet) => {
    ;(Array.isArray(sheet.tables) ? sheet.tables : []).forEach(pushTable)
  })

  return tablesByPage
}

const ImportPreviewModal = ({
  open,
  onClose,
  onParseStarted,
  onImported,
  projectId,
  docId,
  parseResult,
  documentView,
  project,
  url
}) => {
  const sheets = (parseResult && Array.isArray(parseResult.sheets)) ? parseResult.sheets : []
  const buildings = (project && Array.isArray(project.buildings)) ? project.buildings : []
  const [currentView, setCurrentView] = React.useState(documentView || null)
  const canonicalPages = Array.isArray(currentView && currentView.pages) ? currentView.pages : []

  const [activeSheet, setActiveSheet] = React.useState(0)
  const [activeTable, setActiveTable] = React.useState(0)
  const [tableStates, setTableStates] = React.useState({})
  const [importing, setImporting] = React.useState(false)
  const [error, setError] = React.useState('')
  const [importResult, setImportResult] = React.useState(null)
  const [visualReview, setVisualReview] = React.useState(false)
  const [rowEditsByTable, setRowEditsByTable] = React.useState({})
  const [addedRowsByTable, setAddedRowsByTable] = React.useState({})
  const [activeRowIndex, setActiveRowIndex] = React.useState(0)

  // Page selection state (for classified/large plan sets)
  const baseIsClassifiedOnly = parseResult && parseResult.classificationOnly === true
  const fallbackSheetIndex = React.useMemo(
    () => ((parseResult && Array.isArray(parseResult.sheetIndex)) ? parseResult.sheetIndex : []),
    [parseResult]
  )
  const sheetIndex = React.useMemo(() => (
    canonicalPages.length > 0
      ? canonicalPages.map((page) => ({
          page: page.pageNumber,
          sheetNumber: page.sheetNumber || '',
          discipline: page.discipline || 'other',
          title: page.title || `Page ${page.pageNumber}`,
          hasSchedule: Boolean(page.hasSchedule)
        }))
      : fallbackSheetIndex
  ), [canonicalPages, fallbackSheetIndex])
  const [selectedPages, setSelectedPages] = React.useState(() => {
    // Pre-select pages with schedules
    return new Set(sheetIndex.filter(si => si.hasSchedule).map(si => si.page))
  })
  const [parsing, setParsing] = React.useState(false)
  const [localParseResult, setLocalParseResult] = React.useState(null)

  // Use localParseResult (after page parse) or the prop
  const effectiveParseResult = localParseResult || parseResult
  const pageSummary = currentView && currentView.pageSummary ? currentView.pageSummary : null
  const extractedTableFallbacksByPage = React.useMemo(
    () => buildExtractedTableFallbacksByPage(effectiveParseResult || {}),
    [effectiveParseResult]
  )
  const canonicalSheets = React.useMemo(() => {
    return canonicalPages
      .map((page) => {
        const pageNumber = Number(page.pageNumber)
        const pageTables = Array.isArray(page.tables) ? page.tables : []
        const hasOnlyPlaceholderTables = pageTables.length > 0 && pageTables.every(isManualReviewPlaceholderTable)
        const fallbackTables = Number.isFinite(pageNumber) ? (extractedTableFallbacksByPage.get(pageNumber) || []) : []
        const tables = (pageTables.length === 0 || hasOnlyPlaceholderTables) && fallbackTables.length > 0
          ? fallbackTables
          : pageTables

        return {
          name: page.title || page.sheetNumber || `PDF Page ${page.pageNumber}`,
          pageNumber: page.pageNumber,
          sheetNumber: page.sheetNumber || '',
          discipline: page.discipline || 'other',
          tables,
          reviewArtifacts: Array.isArray(page.reviewArtifacts) ? page.reviewArtifacts : [],
          reviewSummary: page.reviewSummary || null,
          previewImage: page.previewImage || null
        }
      })
      .filter(sheet => Array.isArray(sheet.tables) && sheet.tables.length > 0)
  }, [canonicalPages, extractedTableFallbacksByPage])
  const hasExtractedTables = canonicalSheets.length > 0 || (
    effectiveParseResult &&
    effectiveParseResult.classificationOnly !== true &&
    Array.isArray(effectiveParseResult.sheets) &&
    effectiveParseResult.sheets.length > 0
  )
  const readyForImport = Boolean((pageSummary && pageSummary.readyForImport) || hasExtractedTables)
  const readyForSelection = Boolean((pageSummary && pageSummary.readyForSelection) || sheetIndex.length > 0 || baseIsClassifiedOnly)
  const isSelectionPhase = readyForSelection && !readyForImport && !localParseResult
  const effectiveSheets = React.useMemo(() => (
    canonicalSheets.length > 0
      ? canonicalSheets
      : ((effectiveParseResult && Array.isArray(effectiveParseResult.sheets)) ? effectiveParseResult.sheets : sheets)
  ), [canonicalSheets, effectiveParseResult, sheets])
  const totalPageCount = Number((currentView && currentView.pageSummary && currentView.pageSummary.totalPages) || parseResult?.pageCount || sheetIndex.length || 0)

  const defaultBuilding = React.useMemo(() => {
    if (buildings.length === 1) {
      return typeof buildings[0] === 'string' ? buildings[0] : (buildings[0].name || '')
    }
    if (buildings.length === 0) return 'Main'
    return ''
  }, [buildings])

  const sectionSuggestions = React.useMemo(() => {
    const values = new Set()

    const push = (value) => {
      const text = String(value || '').trim()
      if (text) values.add(text)
    }

    if (project && project.bySection && typeof project.bySection === 'object') {
      Object.keys(project.bySection).forEach(push)
    }

    effectiveSheets.forEach((sheet) => {
      ;(sheet.tables || []).forEach((table) => {
        ;(table.rows || []).forEach((row) => {
          push(row.section)
          if (row.category) push(`Planting - ${row.category}`)
        })
      })
    })

    ;[
      'Irrigation',
      'Irrigation - Mainline',
      'Irrigation - Sleeving',
      'Lighting',
      'Planting - Trees',
      'Planting - Shrubs',
      'Planting - Accents',
      'Planting - Groundcover',
      'Hardscape',
      'Hardscape - Materials'
    ].forEach(push)

    return Array.from(values).sort((a, b) => a.localeCompare(b))
  }, [project, effectiveSheets])

  const getKey = (s, t) => `${s}:${t}`

  const getState = (s, t) => {
    const k = getKey(s, t)
    if (tableStates[k]) return tableStates[k]
    return { building: defaultBuilding, section: '', skipSet: new Set() }
  }

  const getRowEdits = (s, t) => {
    return rowEditsByTable[getKey(s, t)] || {}
  }

  const handleRowEdit = (rowIndex, field, value) => {
    const k = getKey(activeSheet, activeTable)
    setRowEditsByTable(prev => {
      const tableEdits = { ...(prev[k] || {}) }
      if (!tableEdits[rowIndex]) tableEdits[rowIndex] = {}
      tableEdits[rowIndex] = { ...tableEdits[rowIndex], [field]: value }
      return { ...prev, [k]: tableEdits }
    })
  }

  const getAddedRows = (s, t) => {
    return addedRowsByTable[getKey(s, t)] || []
  }

  const handleAddRow = () => {
    const k = getKey(activeSheet, activeTable)
    setAddedRowsByTable(prev => ({
      ...prev,
      [k]: [...(prev[k] || []), { description: '', unit: 'EA', quantity: 1, section: '' }]
    }))
  }

  const handleEditAddedRow = (addedIndex, field, value) => {
    const k = getKey(activeSheet, activeTable)
    setAddedRowsByTable(prev => {
      const list = [...(prev[k] || [])]
      list[addedIndex] = { ...list[addedIndex], [field]: value }
      return { ...prev, [k]: list }
    })
  }

  const handleRemoveAddedRow = (addedIndex) => {
    const k = getKey(activeSheet, activeTable)
    setAddedRowsByTable(prev => {
      const list = [...(prev[k] || [])]
      list.splice(addedIndex, 1)
      return { ...prev, [k]: list }
    })
  }

  const togglePage = (pageNum) => {
    setSelectedPages(prev => {
      const next = new Set(prev)
      if (next.has(pageNum)) next.delete(pageNum)
      else next.add(pageNum)
      return next
    })
  }

  const selectByDiscipline = (discipline) => {
    const pages = sheetIndex.filter(si => si.discipline === discipline).map(si => si.page)
    setSelectedPages(prev => {
      const next = new Set(prev)
      pages.forEach(p => next.add(p))
      return next
    })
  }

  const handleParsePages = async () => {
    if (selectedPages.size === 0) return
    setParsing(true)
    setError('')
    const pages = Array.from(selectedPages).sort((a, b) => a - b)
    try {
      const { data } = await axios.post(
        `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/parse-pages`,
        { pages, postExtractMode: 'manual_review' }
      )
      setParsing(false)
      if (onParseStarted) onParseStarted({ docId, pages, message: data?.message })
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Parse failed')
      setParsing(false)
    }
  }

  const updateState = (s, t, next) => {
    setTableStates(prev => ({ ...prev, [getKey(s, t)]: next }))
  }

  // Load saved review edits when modal opens
  React.useEffect(() => {
    if (!open) {
      setActiveSheet(0)
      setActiveTable(0)
      setActiveRowIndex(0)
      setTableStates({})
      setRowEditsByTable({})
      setAddedRowsByTable({})
      setVisualReview(false)
      setError('')
      setImportResult(null)
      return
    }
    const saved = documentView && documentView.document && documentView.document.reviewEdits
    if (!saved) return
    if (saved.rowEdits && Object.keys(saved.rowEdits).length > 0) {
      setRowEditsByTable(saved.rowEdits)
    }
    if (saved.addedRows && Object.keys(saved.addedRows).length > 0) {
      setAddedRowsByTable(saved.addedRows)
    }
    if (saved.tableStates && Object.keys(saved.tableStates).length > 0) {
      // Restore tableStates but rebuild skipSet from arrays
      const restored = {}
      for (const [k, v] of Object.entries(saved.tableStates)) {
        restored[k] = {
          ...v,
          skipSet: new Set(Array.isArray(v.skipSet) ? v.skipSet : [])
        }
      }
      setTableStates(restored)
    }
  }, [open]) // eslint-disable-line

  // Auto-save review edits (debounced)
  React.useEffect(() => {
    if (!open || !projectId || !docId) return undefined
    const hasEdits = Object.keys(rowEditsByTable).length > 0 ||
      Object.keys(addedRowsByTable).length > 0 ||
      Object.keys(tableStates).length > 0
    if (!hasEdits) return undefined

    const timer = setTimeout(() => {
      // Serialize skipSets to arrays for JSON
      const serializedStates = {}
      for (const [k, v] of Object.entries(tableStates)) {
        serializedStates[k] = {
          ...v,
          skipSet: v.skipSet ? Array.from(v.skipSet) : []
        }
      }
      axios.put(
        `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/review-edits`,
        { rowEdits: rowEditsByTable, addedRows: addedRowsByTable, tableStates: serializedStates }
      ).catch(() => {})
    }, 1500)
    return () => clearTimeout(timer)
  }, [open, projectId, docId, url, rowEditsByTable, addedRowsByTable, tableStates])

  React.useEffect(() => {
    setCurrentView(documentView || null)
  }, [documentView])

  React.useEffect(() => {
    const initial = new Set(sheetIndex.filter(si => si.hasSchedule).map(si => si.page))
    setSelectedPages(initial)
  }, [sheetIndex])

  React.useEffect(() => {
    if (!open || !parsing || !projectId || !docId) return undefined
    try {
      const { subscribe } = require('../../SocketIo/Socket')
      const unsub = subscribe(`doc_upload_${projectId}`, (data) => {
        if (!data || !data.stage) return
        const eventDocId = data.docId || data.documentId
        if (eventDocId && String(eventDocId) !== String(docId)) return
        if (data.stage !== 'complete') return
        if (data.parseStatus === 'parsed') {
          axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/view`).then((res) => {
            setCurrentView(res.data)
            setLocalParseResult(res.data.parseResult || null)
            setParsing(false)
          }).catch(() => {
            setError('Parsed, but failed to refresh document view')
            setParsing(false)
          })
          return
        }
        setError(data.message || 'Parse failed')
        setParsing(false)
      })
      return unsub
    } catch (e) {
      return undefined
    }
  }, [open, parsing, projectId, docId, url])

  React.useEffect(() => {
    setActiveRowIndex(0)
  }, [activeSheet, activeTable])

  const handleSheetChange = (_, v) => {
    setActiveSheet(v)
    setActiveTable(0)
    setActiveRowIndex(0)
  }


  const getCurrentTableValidation = () => {
    const sheet = effectiveSheets[activeSheet]
    const table = sheet && sheet.tables && sheet.tables[activeTable]
    const state = getState(activeSheet, activeTable)
    const rows = Array.isArray(table?.rows) ? table.rows : []
    const edits = getRowEdits(activeSheet, activeTable)
    const invalidRows = []
    let importableCount = 0

    rows.forEach((row, idx) => {
      if (state.skipSet && state.skipSet.has(idx)) return
      if (!isPreviewRowImportable(row, edits[idx])) invalidRows.push(idx)
      else importableCount++
    })

    return {
      invalidRows,
      importableCount,
      selectedCount: rows.filter((_, idx) => !(state.skipSet && state.skipSet.has(idx))).length
    }
  }

  const handleImport = () => {
    const sheet = effectiveSheets[activeSheet]
    const table = sheet && sheet.tables && sheet.tables[activeTable]
    if (!table) return

    const state = getState(activeSheet, activeTable)
    if (!state.building) {
      setError('Please select a building before importing.')
      return
    }
    const rows = Array.isArray(table.rows) ? table.rows : []
    const skipRows = Array.from(state.skipSet || [])
    const addedRows = getAddedRows(activeSheet, activeTable)
    if (rows.length - skipRows.length + addedRows.length <= 0) {
      setError('No rows selected to import.')
      return
    }

    const validation = getCurrentTableValidation()
    const importableAddedRows = addedRows.filter(r => r.description && r.unit && (Number(r.quantity) > 0 || /^(ls|allow)$/i.test(r.unit))).length
    if (validation.importableCount + importableAddedRows <= 0) {
      setError('No valid rows to import.')
      return
    }

    setImporting(true)
    setError('')
    setImportResult(null)

    const payload = {
      sheetIndex: activeSheet,
      tableIndex: activeTable,
      building: state.building,
      skipRows
    }
    if (Number.isFinite(Number(currentSheet.pageNumber))) {
      payload.pageNumber = Number(currentSheet.pageNumber)
    }
    if (state.section && state.section.trim()) {
      payload.section = state.section.trim()
    }

    // Include row overrides if any edits exist
    const edits = getRowEdits(activeSheet, activeTable)
    if (Object.keys(edits).length > 0) {
      payload.rowOverrides = edits
    }

    // Include manually added rows
    if (addedRows.length > 0) {
      payload.addedRows = addedRows
    }

    axios.post(
      `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/import-items`,
      payload
    ).then(res => {
      setImporting(false)
      const count = res.data?.imported || 0
      const blockedRows = Array.isArray(res.data?.blockedRows) ? res.data.blockedRows : []
      const result = { imported: count, blockedRows }
      setImportResult(result)
      if (onImported) onImported(count, result)
    }).catch(err => {
      setImporting(false)
      setError(err.response?.data?.error || err.response?.data?.message || 'Import failed. Please try again.')
    })
  }

  if ((!parseResult || sheets.length === 0) && effectiveSheets.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Import Items</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            This document has no parsed tables to import.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  const currentSheet = effectiveSheets[activeSheet] || {}
  const tables = Array.isArray(currentSheet.tables) ? currentSheet.tables : []

  const currentValidation = getCurrentTableValidation()
  const hasBlockedSelectedRows = currentValidation.invalidRows.length > 0
  const currentTable = tables[activeTable]
  const currentState = currentTable ? getState(activeSheet, activeTable) : null
  const currentRows = currentTable && Array.isArray(currentTable.rows) ? currentTable.rows : []
  const currentRow = currentRows[activeRowIndex] || null
  const currentTablePageNumber = Number(
    currentSheet.pageNumber ||
    (currentRows[0] && currentRows[0].page) ||
    (currentTable && currentTable.page) ||
    0
  )
  const currentCanonicalPage = currentTablePageNumber
    ? canonicalPages.find((page) => Number(page.pageNumber) === currentTablePageNumber)
    : null
  const currentAddedRows = getAddedRows(activeSheet, activeTable)
  const importableAddedRows = currentAddedRows.filter(r => r.description && r.unit && (Number(r.quantity) > 0 || /^(ls|allow)$/i.test(r.unit))).length
  const rowsCount = (currentTable && Array.isArray(currentTable.rows) ? currentTable.rows.length : 0) + currentAddedRows.length
  const importCount = currentState ? rowsCount - (currentState.skipSet ? currentState.skipSet.size : 0) : 0
  const validImportCount = currentValidation.importableCount + importableAddedRows
  const canImport = Boolean(currentTable && currentState && currentState.building && validImportCount > 0)

  // Determine if current table supports visual review
  const currentReviewArtifacts = currentTable && Array.isArray(currentTable.reviewArtifacts) && currentTable.reviewArtifacts.length > 0
    ? currentTable.reviewArtifacts
    : (Array.isArray(currentSheet.reviewArtifacts) && currentSheet.reviewArtifacts.length > 0
        ? currentSheet.reviewArtifacts
        : (currentCanonicalPage && Array.isArray(currentCanonicalPage.reviewArtifacts) && currentCanonicalPage.reviewArtifacts.length > 0
            ? currentCanonicalPage.reviewArtifacts
            : null))
  const currentPreviewImage = currentSheet.previewImage || (currentCanonicalPage && currentCanonicalPage.previewImage) || null
  const currentReviewSummary = currentTable && currentTable.reviewSummary
    ? currentTable.reviewSummary
    : (currentSheet.reviewSummary || (currentCanonicalPage && currentCanonicalPage.reviewSummary) || null)

  React.useEffect(() => {
    if (!open) return
    const canShowVisualReview = Boolean(
      currentTablePageNumber &&
      (currentReviewArtifacts || currentPreviewImage)
    )
    if (canShowVisualReview) {
      setVisualReview(true)
    }
  }, [open, currentTablePageNumber, currentPreviewImage, currentReviewArtifacts])

  const handleApplySecondaryQuantity = (rowIndex, quantity, unit) => {
    handleRowEdit(rowIndex, 'quantity', quantity)
    handleRowEdit(rowIndex, 'unit', unit)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={visualReview ? false : 'lg'}
      fullWidth={!visualReview}
      fullScreen={visualReview}
    >
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Import Items from Document</Typography>
          <IconButton size="small" onClick={onClose} disabled={importing}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers style={visualReview ? { display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' } : {}}>
        {/* Plan Set metadata header */}
        {parseResult && parseResult.documentType === 'plan_set' && (
          <div style={{
            padding: '12px 16px',
            marginBottom: visualReview ? 0 : 16,
            borderBottom: '1px solid #e0e0e0'
          }}>
            {parseResult.titleBlock && (
              <div style={{ marginBottom: 8 }}>
                {parseResult.titleBlock.projectName && (
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {parseResult.titleBlock.projectName}
                  </Typography>
                )}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
                  {parseResult.titleBlock.address && (
                    <Typography variant="caption" color="textSecondary">
                      {parseResult.titleBlock.address}
                    </Typography>
                  )}
                  {parseResult.titleBlock.architect && (
                    <Typography variant="caption" color="textSecondary">
                      Architect: {parseResult.titleBlock.architect}
                    </Typography>
                  )}
                  {parseResult.titleBlock.date && (
                    <Typography variant="caption" color="textSecondary">
                      Date: {parseResult.titleBlock.date}
                    </Typography>
                  )}
                </div>
              </div>
            )}
            {sheetIndex.length > 0 && (() => {
              const disciplineCounts = {}
              const scheduleCounts = {}
              sheetIndex.forEach((si) => {
                const d = (si.discipline || 'other').replace(/_/g, ' ')
                disciplineCounts[d] = (disciplineCounts[d] || 0) + 1
                if (si.hasSchedule) scheduleCounts[d] = (scheduleCounts[d] || 0) + 1
              })
              const scheduleTotal = sheetIndex.filter(si => si.hasSchedule).length
              return (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="caption" color="textSecondary">
                    {sheetIndex.length} pages{scheduleTotal > 0 ? ` (${scheduleTotal} with schedules)` : ''}:
                  </Typography>
                  {Object.entries(disciplineCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([discipline, count]) => {
                      const withSchedule = scheduleCounts[discipline] || 0
                      return (
                        <Chip
                          key={discipline}
                          size="small"
                          label={`${discipline} (${count})${withSchedule ? ` · ${withSchedule} sched` : ''}`}
                          style={{
                            backgroundColor: withSchedule > 0 ? '#e8f5e9' : '#f5f5f5',
                            color: withSchedule > 0 ? '#2e7d32' : '#757575',
                            fontSize: 11
                          }}
                        />
                      )
                    })}
                </div>
              )
            })()}
          </div>
        )}

        {/* Page Selection for classified (large) plan sets */}
        {isSelectionPhase && (
          <div style={{ padding: visualReview ? '0 16px' : 0 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
              Select Pages to Extract ({totalPageCount} total pages)
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
              Extraction runs in the background. Start it here, then close this window and keep working. Visual review becomes available after extraction finishes.
            </Typography>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" onClick={() => selectByDiscipline('landscape')}>
                All Landscape
              </Button>
              <Button size="small" variant="outlined" onClick={() => selectByDiscipline('irrigation')}>
                All Irrigation
              </Button>
              <Button size="small" variant="outlined" onClick={() => selectByDiscipline('tree_inventory')}>
                All Tree Inventory
              </Button>
              <Button size="small" variant="outlined" onClick={() => selectByDiscipline('hardscape')}>
                All Hardscape
              </Button>
              <Button size="small" variant="outlined"
                onClick={() => setSelectedPages(new Set(sheetIndex.filter(si => si.hasSchedule).map(si => si.page)))}>
                All with Schedules
              </Button>
            </div>
            <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 4 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Page</TableCell>
                    <TableCell>Discipline</TableCell>
                    <TableCell>Sheet #</TableCell>
                    <TableCell>Schedule</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sheetIndex.map(si => {
                    const disciplineColors = {
                      landscape: '#2e7d32', irrigation: '#1565c0', hardscape: '#e65100',
                      tree_inventory: '#6a1b9a', lighting: '#f9a825', demolition: '#c62828',
                      cover: '#546e7a', notes: '#546e7a', detail: '#546e7a', other: '#9e9e9e'
                    }
                    return (
                      <TableRow key={si.page} hover
                        style={{ backgroundColor: selectedPages.has(si.page) ? '#e8f5e9' : undefined }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedPages.has(si.page)}
                            onChange={() => togglePage(si.page)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{si.page}</TableCell>
                        <TableCell>
                          <Chip size="small" label={si.discipline.replace('_', ' ')}
                            style={{
                              backgroundColor: disciplineColors[si.discipline] || '#9e9e9e',
                              color: '#fff', fontSize: 11
                            }} />
                        </TableCell>
                        <TableCell>{si.sheetNumber || '—'}</TableCell>
                        <TableCell>{si.hasSchedule ? 'Yes' : '—'}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
            {parsing && (
              <div style={{ marginTop: 16 }}>
                <LinearProgress />
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                  Starting background extraction for {selectedPages.size} pages...
                </Typography>
              </div>
            )}
            {error && (
              <Typography color="error" variant="body2" style={{ marginTop: 8 }}>{error}</Typography>
            )}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleParsePages}
                disabled={selectedPages.size === 0 || parsing}
              >
                {parsing ? 'Starting...' : `Extract ${selectedPages.size} Selected Pages`}
              </Button>
            </div>
          </div>
        )}

        {/* Normal import flow (after parsing is done) */}
        {!isSelectionPhase && effectiveSheets.length > 1 && (
          <Paper elevation={0} style={{ border: '1px solid #eeeeee', marginBottom: 16, ...(visualReview ? { margin: '0 16px 8px' } : {}) }}>
            <Tabs
              value={activeSheet}
              onChange={handleSheetChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {effectiveSheets.map((s, i) => (
                <Tab key={i} label={s.name || `Sheet ${i + 1}`} />
              ))}
            </Tabs>
          </Paper>
        )}

        {/* Split layout wrapper */}
        <div style={visualReview ? { display: 'flex', flex: 1, overflow: 'hidden' } : {}}>
          {/* Left pane: table */}
          <div style={visualReview ? { flex: 1, minHeight: 0, overflow: 'hidden', borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column' } : {}}>
            <Paper elevation={0} style={{ padding: 16, border: visualReview ? 'none' : '1px solid #eeeeee', display: visualReview ? 'flex' : 'block', flexDirection: visualReview ? 'column' : undefined, flex: visualReview ? 1 : undefined, minHeight: visualReview ? 0 : undefined, overflow: visualReview ? 'hidden' : undefined }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
                {currentSheet.name || `Sheet ${activeSheet + 1}`}
              </Typography>

              {tables.length > 1 && (
                <Tabs
                  value={activeTable}
                  onChange={(_, v) => setActiveTable(v)}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                  style={{ marginBottom: 16, borderBottom: '1px solid #eeeeee' }}
                >
                  {tables.map((_, i) => (
                    <Tab key={i} label={`Table ${i + 1}`} />
                  ))}
                </Tabs>
              )}

              {currentTable ? (
                <ImportTableCard
                  sheetIndex={activeSheet}
                  tableIndex={activeTable}
                  table={currentTable}
                  buildings={buildings}
                  state={currentState}
                  onChange={updateState}
                  reviewSummary={currentReviewSummary}
                  hasVisualReview={Boolean(currentTablePageNumber && (currentReviewArtifacts || currentPreviewImage))}
                  onToggleVisualReview={() => setVisualReview(v => !v)}
                  rowEdits={getRowEdits(activeSheet, activeTable)}
                  onRowEdit={handleRowEdit}
                  selectedRowIndex={activeRowIndex}
                  onSelectRow={setActiveRowIndex}
                  sectionSuggestions={sectionSuggestions}
                  fillAvailableHeight={visualReview}
                  addedRows={currentAddedRows}
                  onAddRow={handleAddRow}
                  onEditAddedRow={handleEditAddedRow}
                  onRemoveAddedRow={handleRemoveAddedRow}
                />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No tables found in this sheet.
                </Typography>
              )}
            </Paper>
          </div>

          {/* Right pane: source crop (only when visual review active) */}
          {visualReview && currentTablePageNumber && (currentReviewArtifacts || currentPreviewImage) && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <SourceCropPanel
                baseUrl={url}
                projectId={projectId}
                docId={docId}
                sheetIndex={activeSheet}
                pageNumber={currentTablePageNumber}
                tableIndex={activeTable}
                reviewArtifacts={currentReviewArtifacts}
                row={currentRow}
                rowIndex={activeRowIndex}
                onApplySecondaryQuantity={handleApplySecondaryQuantity}
              />
            </div>
          )}
        </div>

        {error && (
          <div style={{
            marginTop: 16,
            padding: '12px 16px',
            backgroundColor: '#ffebee',
            borderRadius: 4,
            borderLeft: '4px solid #c62828',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <ErrorOutline style={{ color: '#c62828' }} />
            <Typography variant="body2" color="error">{error}</Typography>
          </div>
        )}
        {importResult && (
          <div style={{
            marginTop: 16,
            padding: '12px 16px',
            backgroundColor: importResult.blockedRows.length > 0 ? '#fff8e1' : '#e8f5e9',
            borderRadius: 4,
            borderLeft: `4px solid ${importResult.blockedRows.length > 0 ? '#f57f17' : '#2e7d32'}`
          }}>
            <Typography variant="body2">
              Imported {importResult.imported} row{importResult.imported === 1 ? '' : 's'}
              {importResult.blockedRows.length > 0
                ? `; ${importResult.blockedRows.length} selected row${importResult.blockedRows.length === 1 ? '' : 's'} left for review.`
                : '.'}
            </Typography>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <div style={{ marginRight: 'auto', marginLeft: 8, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="caption" color="textSecondary">
            {currentTable ? `${importCount} of ${rowsCount} rows selected` : ''}
          </Typography>
          {hasBlockedSelectedRows && (
            <Typography variant="caption" color="error">
              {currentValidation.invalidRows.length} selected row{currentValidation.invalidRows.length === 1 ? '' : 's'} without a unit or quantity will stay for review.
            </Typography>
          )}
        </div>
        <Button onClick={onClose} disabled={importing}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          color="primary"
          disabled={!canImport || importing}
          startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <GetAppIcon />}
        >
          {importing ? 'Importing…' : 'Import Items'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImportPreviewModal
